import { Button, FormControl, FormControlLabel, Switch, TextField } from '@material-ui/core';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { Autocomplete } from '@material-ui/lab';
import { yupResolver } from '@hookform/resolvers/yup';
import classnames from 'classnames';
import { Buyer, ExpeditionFilters, ExpeditionFiltersEncoded, Material } from 'types';
import { useBuyers, useDistinctRepartitionStatus, useMaterials } from 'hooks';
import DatePicker from 'components/CustomInputs/DatePicker';
import NextIcon from 'components/Icons/NextIcon';
import { atEndOfDay, atStartOfDay } from 'utils/date';
import { transformObjectId } from 'services/apiPlatformTransformer';
import { DEFAULT_ITEMS_PER_SELECT, TableFilterContext } from 'tableFilterContext';
import { dateFilterBarSchema } from 'components/Generic/DateFilterBar.yup';
import { formatExpeditionQueryAsFilter } from 'utils/filters';
import {
  FILTER_MISE_COHERENCE_CENTRE_TRI,
  FILTER_MISE_COHERENCE_CENTRE_TRI_QUERY_VALUE,
  FILTER_MISE_COHERENCE_DIFFERENTE,
  FILTER_MISE_COHERENCE_DIFFERENTE_QUERY_VALUE,
  FILTER_MISE_COHERENCE_REPRENEUR,
  FILTER_MISE_COHERENCE_REPRENEUR_QUERY_VALUE,
  FILTER_MISE_COHERENCE_TOUS,
} from './constants';
import { useStyles } from './ExpeditionFilterBar.style';

type ExpeditionFilterBarProps = {
  minStartDate: Date;
  maxEndDate: Date;
  defaultStartDate?: Date;
  defaultEndDate?: Date;
};

export const ExpeditionFilterBar: React.FC<ExpeditionFilterBarProps> = ({ minStartDate, maxEndDate }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { t: pt } = useTranslation('translation', { keyPrefix: 'expeditions.filter' });
  const [isReady, setReady] = useState<boolean>(false);
  const { dispatch, search } = useContext(TableFilterContext);
  const [defaultMaterialValue, setDefaultMaterialValue] = useState<Material | null>(null);
  const [defaultBuyerValue, setDefaultBuyerValue] = useState<Buyer | null>(null);
  const [filterMiseCoherenceSelected, setFilterMiseCoherenceSelected] = useState(1);

  const [minEndDate, setMinEndDate] = useState<Date | null>(minStartDate);
  const [maxStartDate, setMaxStartDate] = useState<Date | null>(maxEndDate);

  const materials = useMaterials({ itemsPerPage: String(DEFAULT_ITEMS_PER_SELECT) });
  const [buyers] = useBuyers({ itemsPerPage: String(DEFAULT_ITEMS_PER_SELECT) });
  const [distinctRepartitionStatus] = useDistinctRepartitionStatus();
  const shouldDisplayFilterMiseCoherenceTous = Boolean(
    distinctRepartitionStatus && 0 < distinctRepartitionStatus.length,
  );
  const shouldDisplayFilterMiseCoherenceCentreTri = distinctRepartitionStatus?.includes(
    FILTER_MISE_COHERENCE_CENTRE_TRI_QUERY_VALUE,
  );
  const shouldDisplayFilterMiseCoherenceRepreneur = distinctRepartitionStatus?.includes(
    Boolean(FILTER_MISE_COHERENCE_REPRENEUR_QUERY_VALUE),
  );
  const shouldDisplayFilterMiseCoherenceDifferente = distinctRepartitionStatus?.includes(
    Boolean(FILTER_MISE_COHERENCE_DIFFERENTE_QUERY_VALUE),
  );

  const schema = useMemo(
    () => dateFilterBarSchema(t, atStartOfDay(minStartDate), atEndOfDay(maxEndDate)),
    [t, minStartDate, maxEndDate],
  );

  const { control, trigger, handleSubmit, reset } = useForm<ExpeditionFilters>({
    mode: 'onBlur',
    defaultValues: {
      startDate: null,
      endDate: null,
      invalidOnly: false,
    },
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    const query: ExpeditionFiltersEncoded = search ? JSON.parse(atob(search)) : {};
    const filters: ExpeditionFiltersEncoded = {
      startDate: query.startDate ? query.startDate : null,
      endDate: query.endDate ? query.endDate : null,
      material: query.material ?? null,
      buyer: query.buyer ?? null,
      invalidOnly: FILTER_MISE_COHERENCE_TOUS !== filterMiseCoherenceSelected ? false : query.invalidOnly,
    };

    if (FILTER_MISE_COHERENCE_CENTRE_TRI === filterMiseCoherenceSelected) {
      filters.isMiseEnCoherenceAccepted = FILTER_MISE_COHERENCE_CENTRE_TRI_QUERY_VALUE;
    } else if (FILTER_MISE_COHERENCE_REPRENEUR === filterMiseCoherenceSelected) {
      filters.isMiseEnCoherenceAccepted = FILTER_MISE_COHERENCE_REPRENEUR_QUERY_VALUE;
    } else if (FILTER_MISE_COHERENCE_DIFFERENTE === filterMiseCoherenceSelected) {
      filters.isMiseEnCoherenceAccepted = FILTER_MISE_COHERENCE_DIFFERENTE_QUERY_VALUE;
    }

    dispatch({
      type: 'set_search',
      payload: btoa(JSON.stringify(filters)),
    });
  }, [filterMiseCoherenceSelected, search, dispatch]);

  useEffect(() => {
    if (!materials || !buyers) return;
    if (!search) {
      setReady(true);
      return;
    }
    const query: ExpeditionFiltersEncoded = JSON.parse(atob(search));
    if (query.material !== null) {
      setDefaultMaterialValue(materials.find(material => transformObjectId(material) === query.material) ?? null);
    }
    if (query.buyer !== null) {
      setDefaultBuyerValue(buyers.find(buyer => transformObjectId(buyer) === query.buyer) ?? null);
    }
    setReady(true);
  }, [search, setReady, materials, buyers, setDefaultBuyerValue, setDefaultMaterialValue]);

  useEffect(() => {
    if (!search) return;
    const query: ExpeditionFiltersEncoded = JSON.parse(atob(search));
    const filter = formatExpeditionQueryAsFilter(query);
    reset(filter);

    if (undefined !== query.isMiseEnCoherenceAccepted) {
      if (FILTER_MISE_COHERENCE_CENTRE_TRI_QUERY_VALUE === query.isMiseEnCoherenceAccepted) {
        setFilterMiseCoherenceSelected(FILTER_MISE_COHERENCE_CENTRE_TRI);
      } else if (FILTER_MISE_COHERENCE_REPRENEUR_QUERY_VALUE === query.isMiseEnCoherenceAccepted) {
        setFilterMiseCoherenceSelected(FILTER_MISE_COHERENCE_REPRENEUR);
      } else if (FILTER_MISE_COHERENCE_DIFFERENTE_QUERY_VALUE === query.isMiseEnCoherenceAccepted) {
        setFilterMiseCoherenceSelected(FILTER_MISE_COHERENCE_DIFFERENTE);
      }
    }
  }, [search, reset]);

  useEffect(() => {
    if (
      undefined === shouldDisplayFilterMiseCoherenceCentreTri ||
      undefined === shouldDisplayFilterMiseCoherenceRepreneur ||
      undefined === shouldDisplayFilterMiseCoherenceDifferente
    ) {
      return;
    }

    if (
      (filterMiseCoherenceSelected === FILTER_MISE_COHERENCE_CENTRE_TRI &&
        !shouldDisplayFilterMiseCoherenceCentreTri) ||
      (filterMiseCoherenceSelected === FILTER_MISE_COHERENCE_REPRENEUR && !shouldDisplayFilterMiseCoherenceRepreneur) ||
      (filterMiseCoherenceSelected === FILTER_MISE_COHERENCE_DIFFERENTE && !shouldDisplayFilterMiseCoherenceDifferente)
    ) {
      setFilterMiseCoherenceSelected(FILTER_MISE_COHERENCE_TOUS);
    }
  }, [
    shouldDisplayFilterMiseCoherenceCentreTri,
    shouldDisplayFilterMiseCoherenceRepreneur,
    shouldDisplayFilterMiseCoherenceDifferente,
    filterMiseCoherenceSelected,
  ]);

  const saveSearchParams = useCallback(
    (e: ExpeditionFilters) => {
      dispatch({
        type: 'set_search',
        payload: btoa(
          JSON.stringify({
            startDate: e.startDate ? e.startDate.getTime() : null,
            endDate: e.endDate ? e.endDate.getTime() : null,
            material: e.material ?? null,
            buyer: e.buyer ?? null,
            invalidOnly: e.invalidOnly,
          }),
        ),
      });
    },
    [dispatch],
  );

  const submitPeriod = useCallback(
    (e: ExpeditionFilters) => {
      saveSearchParams(e);
    },
    [saveSearchParams],
  );

  return (
    <form onSubmit={handleSubmit(submitPeriod)}>
      <div className={classes.fieldLine}>
        {isReady && (
          <>
            <span>
              <Controller
                name="material"
                control={control}
                render={({ field: { ref, onChange, ...fieldProps } }) => (
                  <Autocomplete
                    id="material"
                    options={materials ?? []}
                    size="small"
                    getOptionLabel={material => material.shortName}
                    defaultValue={defaultMaterialValue}
                    classes={{ root: classes.autocomplete, endAdornment: classes.autocompleteControls }}
                    noOptionsText={t('noResults')}
                    onChange={(_e, value: Material | null) => {
                      onChange({ target: { value: transformObjectId(value) } });
                    }}
                    renderInput={params => (
                      <TextField
                        {...params}
                        className={classes.textField}
                        {...fieldProps}
                        placeholder={pt('materials')}
                        variant="outlined"
                      />
                    )}
                  />
                )}
              />
            </span>
            <span>
              <Controller
                name="buyer"
                control={control}
                render={({ field: { ref, onChange, ...fieldProps } }) => (
                  <Autocomplete
                    id="buyer"
                    size="small"
                    options={buyers ?? []}
                    getOptionLabel={buyer => buyer.name}
                    defaultValue={defaultBuyerValue}
                    classes={{ root: classes.autocomplete, endAdornment: classes.autocompleteControls }}
                    noOptionsText={t('noResults')}
                    onChange={(_e, value: Buyer | null) => {
                      onChange({ target: { value: transformObjectId(value) } });
                    }}
                    renderInput={params => (
                      <TextField
                        {...params}
                        className={classes.textField}
                        {...fieldProps}
                        placeholder={pt('buyer')}
                        variant="outlined"
                      />
                    )}
                  />
                )}
              />
            </span>
          </>
        )}
        <span className={classes.datePicker}>
          <Controller
            name="startDate"
            control={control}
            render={({ field: { ref, onChange, ...fieldProps }, fieldState }) => (
              <FormControl variant="outlined">
                <DatePicker
                  id="startDate"
                  label={t('startDate')}
                  error={fieldState.invalid}
                  helperText={fieldState.error?.message}
                  inputRef={ref}
                  InputLabelProps={{ required: false }}
                  inputVariant="standard"
                  data-qa-id="start-date-filter"
                  maxDate={maxStartDate}
                  minDate={minStartDate}
                  onAccept={() => {
                    trigger('startDate');
                  }}
                  onChange={e => {
                    onChange(e);
                    if (!e || (!!e && e < minStartDate)) {
                      setMinEndDate(minStartDate);
                      return;
                    }
                    setMinEndDate(e);
                  }}
                  {...fieldProps}
                />
              </FormControl>
            )}
          />
        </span>
        <span className={classes.datePicker}>
          <Controller
            name="endDate"
            control={control}
            render={({ field: { ref, onChange, ...fieldProps }, fieldState }) => (
              <FormControl variant="outlined">
                <DatePicker
                  id="endDate"
                  label={t('endDate')}
                  error={fieldState.invalid}
                  data-qa-id="end-date-filter"
                  helperText={fieldState.error?.message}
                  maxDate={maxEndDate}
                  minDate={minEndDate}
                  inputRef={ref}
                  inputVariant="standard"
                  InputLabelProps={{ required: false }}
                  onAccept={() => {
                    trigger('endDate');
                  }}
                  onChange={e => {
                    onChange(e);
                    if (!e || (!!e && e > maxEndDate)) {
                      setMaxStartDate(maxEndDate);
                      return;
                    }
                    setMaxStartDate(e);
                  }}
                  {...fieldProps}
                />
              </FormControl>
            )}
          />
        </span>
        <Button style={{ marginTop: 12 }} size="small" type="submit" variant="text">
          <div className={classes.searchButton}>{t('search')}</div>
          <NextIcon className={classes.searchButtonIcon} />
        </Button>
      </div>
      <div className={classes.containerBottom}>
        <div className={classes.filterMiseCoherence}>
          {shouldDisplayFilterMiseCoherenceTous && (
            <div
              className={classnames({
                [classes.filterMiseCoherenceElementSelectedFirst]:
                  FILTER_MISE_COHERENCE_TOUS === filterMiseCoherenceSelected,
              })}
            >
              <Button
                className={classes.filterMiseCoherenceElementFirst}
                variant="outlined"
                onClick={() => setFilterMiseCoherenceSelected(FILTER_MISE_COHERENCE_TOUS)}
              >
                {pt('miseEnCoherence.tous')}
              </Button>
            </div>
          )}
          {shouldDisplayFilterMiseCoherenceCentreTri && (
            <div
              className={classnames({
                [classes.filterMiseCoherenceElementSelectedSecond]:
                  FILTER_MISE_COHERENCE_CENTRE_TRI === filterMiseCoherenceSelected,
              })}
            >
              <Button
                className={classes.filterMiseCoherenceElementSecond}
                variant="outlined"
                onClick={() => setFilterMiseCoherenceSelected(FILTER_MISE_COHERENCE_CENTRE_TRI)}
              >
                {pt('miseEnCoherence.repartitionCentreTriRetenue')}
              </Button>
            </div>
          )}
          {shouldDisplayFilterMiseCoherenceRepreneur && (
            <div
              className={classnames({
                [classes.filterMiseCoherenceElementSelectedThird]:
                  FILTER_MISE_COHERENCE_REPRENEUR === filterMiseCoherenceSelected,
              })}
            >
              <Button
                className={classes.filterMiseCoherenceElementThird}
                variant="outlined"
                onClick={() => setFilterMiseCoherenceSelected(FILTER_MISE_COHERENCE_REPRENEUR)}
              >
                {pt('miseEnCoherence.repartitionRepreneurRetenue')}
              </Button>
            </div>
          )}
          {shouldDisplayFilterMiseCoherenceDifferente && (
            <div
              className={classnames({
                [classes.filterMiseCoherenceElementSelectedFourth]:
                  FILTER_MISE_COHERENCE_DIFFERENTE === filterMiseCoherenceSelected,
              })}
            >
              <Button
                className={classes.filterMiseCoherenceElementFourth}
                variant="outlined"
                onClick={() => setFilterMiseCoherenceSelected(FILTER_MISE_COHERENCE_DIFFERENTE)}
              >
                {pt('miseEnCoherence.repartitionDifferente')}
              </Button>
            </div>
          )}
        </div>
        <div className={classes.switch}>
          <Controller
            name="invalidOnly"
            control={control}
            render={({ field: { ref, onChange, value, ...fieldProps } }) => (
              <FormControlLabel
                inputRef={ref}
                {...fieldProps}
                control={<Switch color="primary" checked={value} />}
                label={pt('invalidOnly')}
                onChange={(_e, v: boolean) => {
                  onChange({ target: { value: v } });
                  if (v) setFilterMiseCoherenceSelected(1);
                  handleSubmit(submitPeriod)();
                }}
              />
            )}
          />
        </div>
      </div>
    </form>
  );
};
